export interface DocumentGenerationRequest {
  text: string;
  format: "docx" | "pdf" | "html" | "odt";
  filename: string;
}

export interface DocumentGenerationResponse {
  success: boolean;
  download_url: string;
  message: string;
  expires_at: string;
}

import { translateApiError } from "@/lib/auth-error-messages";
import { supabase } from "@/integrations/supabase/client";
import { hasTelegramInitData } from "@/integrations/telegram/telegramService";

export interface DocumentToMarkdownRequest {
  filename: string;
  file_content: string; // BASE64 encoded
}

export interface DocumentToMarkdownResponse {
  success: boolean;
  markdown_text: string;
  filename: string;
  file_extension: string;
}

export class DocumentService {
  private baseUrl: string;
  private markdownUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_GENERATEDOC_URL;
    this.markdownUrl = import.meta.env.VITE_DOCUMENT_TO_MARKDOWN_URL;

    if (!this.baseUrl) {
      throw new Error("VITE_GENERATEDOC_URL environment variable is not set");
    }
    if (!this.markdownUrl) {
      throw new Error("VITE_DOCUMENT_TO_MARKDOWN_URL environment variable is not set");
    }
  }

  private translateError(error: unknown): string {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return translateApiError(errorMessage);
  }

  async generateDocument(
    request: DocumentGenerationRequest
  ): Promise<DocumentGenerationResponse> {
    const formData = new FormData();
    formData.append("text", request.text);
    formData.append("format", request.format);
    formData.append("filename", request.filename);

    const response = await fetch(this.baseUrl, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error Response:", errorText);
      throw new Error(this.translateError(errorText) || "Failed to generate document");
    }

    return response.json();
  }

  async convertToMarkdown(request: DocumentToMarkdownRequest): Promise<DocumentToMarkdownResponse> {
    const response = await fetch(`${this.markdownUrl}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await this.getAuthToken()}`,
      },
      body: JSON.stringify({
        filename: request.filename,
        file_content: request.file_content,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.detail || `Failed to convert document: ${response.status}`;
      throw new Error(this.translateError(errorMessage));
    }

    return response.json();
  }

  private async getAuthToken(): Promise<string> {
    const { data } = await supabase.auth.getSession();
    if (!data.session?.access_token) {
      throw new Error("Authentication required");
    }
    return data.session.access_token;
  }

  async downloadFile(url: string, filename: string): Promise<void> {
    try {
      console.log("Starting file download process...", { url, filename });

      // Определяем окружение
      const isTelegram = hasTelegramInitData();
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );

      console.log("Environment detection:", {
        isTelegram,
        webAppAvailable: Boolean(window.Telegram?.WebApp),
        initDataExists: Boolean(window.Telegram?.WebApp?.initData),
        userAgent: navigator.userAgent,
        isIOS,
      });

      // Для Telegram Mini App
      if (isTelegram) {
        console.log("Using Telegram Mini App download approach");

        // Используем оригинальный URL без модификаций
        if (window.Telegram?.WebApp?.openLink) {
          console.log("Opening link via Telegram WebApp API:", url);
          window.Telegram.WebApp.openLink(url);
        } else {
          console.log("Fallback to window.open:", url);
          const opened = window.open(url, "_blank");

          // Попап могли заблокировать: жест пользователя потерян
          // на await генерации документа
          if (!opened) {
            console.warn("Попап заблокирован, переход в текущей вкладке");
            window.location.href = url;
          }
        }

        return;
      }

      // Для iOS Safari
      if (isIOS && isSafari) {
        console.log("Using iOS Safari download approach");
        window.location.href = url;
        return;
      }

      // Для остальных браузеров используем стандартный подход
      console.log("Using standard download approach");
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("Download process completed successfully");
    } catch (error) {
      console.error("Detailed error in downloadFile:", {
        error,
        errorMessage: error instanceof Error ? error.message : "Unknown error",
        errorStack: error instanceof Error ? error.stack : undefined,
        url,
        filename,
      });
      throw new Error(
        `Не удалось загрузить файл: ${
          error instanceof Error ? error.message : "неизвестная ошибка"
        }`
      );
    }
  }
}

export const documentService = new DocumentService();
